import { useCallback, useState } from 'react'
import type { Block, Category } from '../data/types'

/** A category picked by hand, or null for "nothing selected". */
type Chosen = { categoryId: string | null } | null

/**
 * Which category a drag paints with.
 *
 * A hand-picked one wins. Otherwise it is whatever is recording: painting
 * usually means filling in around what you are already doing, and having to
 * pick the mode you are currently in was a step that said nothing. Failing
 * both — nothing running, or running something retired off the palette — it is
 * the first category, so a drag works on the first try.
 */
export function paintChoice(
  palette: Category[],
  running: string | null,
  chosen: Chosen,
): string | null {
  if (chosen) return chosen.categoryId
  if (running !== null && palette.some((category) => category.id === running)) return running
  return palette[0]?.id ?? null
}

/**
 * The paint selection for Today and Week.
 *
 * A hand-picked category holds only for as long as the recording it was picked
 * during: a deliberate choice should not be yanked away mid-use, and starting a
 * different block is a new answer to the question of what you are doing.
 */
export function usePaintCategory(
  palette: Category[],
  openBlock: Block | null,
): readonly [string | null, (categoryId: string | null) => void] {
  const [chosen, setChosen] = useState<{ during: string | null; categoryId: string | null } | null>(
    null,
  )
  const during = openBlock?.id ?? null

  const paintId = paintChoice(
    palette,
    openBlock?.categoryId ?? null,
    chosen && chosen.during === during ? { categoryId: chosen.categoryId } : null,
  )

  const setPaintId = useCallback(
    (categoryId: string | null) => setChosen({ during, categoryId }),
    [during],
  )

  return [paintId, setPaintId] as const
}
